import type { RouteRecordRaw } from "vue-router";

import { onIntercomQueueReady } from "@/analytics/impl/intercom";
import AppPage from "@/pages/AppPage/AppPage.vue";
import LoginPage from "@/pages/LoginPage/LoginPage.vue";
import LogoutPage from "@/pages/LogoutPage/LogoutPage.vue";
import MobileInfoPage from "@/pages/MobileInfoPage/MobileInfoPage.vue";
import OnboardPage from "@/pages/OnboardPage/OnboardPage.vue";
import WheelTestPage from "@/pages/WheelTestPage.vue";
import { useCompanyStore } from "@/store/company";

import { assertSmallScreen } from "./guards/assertSmallScreen";
import { beforeLogin } from "./guards/beforeLogin";
import { PARAM } from "./types";

const StatusPage = () => import("@/pages/StatusPage/StatusPage.vue");

export const routes: RouteRecordRaw[] = [
  {
    name: "status",
    path: "/status",
    meta: { title: /*$t*/ "page.status", needsAuth: false, optionalAuth: true },
    component: StatusPage,
  },
  {
    name: "mobile",
    path: "/mobile",
    component: MobileInfoPage,
    beforeEnter: assertSmallScreen,
    meta: { needsAuth: false },
  },
  {
    name: "login",
    path: "/login",
    meta: { title: /*$t*/ "page.login", needsAuth: false },
    component: LoginPage,
    beforeEnter: beforeLogin,
  },
  { path: "/page/login", redirect: { name: "login" } },
  { path: "/page/oauth", redirect: { name: "login" } },
  { name: "app", path: "/app/:session/:team/:board/:name?", component: AppPage },
  {
    name: "onboard",
    path: "/onboard",
    meta: { title: /*$t*/ "page.onboard" },
    component: OnboardPage,
    beforeEnter: () => {
      if (!useCompanyStore().shouldOnboard) {
        return defaultAppRoute();
      }
    },
  },
  // Special route to intercept tour requests from Intercom checklist links
  // (otherwise, Intercom will trigger a navigation event, which reloads the app
  // and usually doesn't actually trigger the tour...)
  {
    name: "tour",
    path: "/tour/:tourId",
    component: AppPage, // Need to have either component or redirect
    beforeEnter: (to, from) => {
      const tourId = Number(to.params.tourId);

      if (typeof tourId !== "number" || isNaN(tourId)) {
        return defaultAppRoute();
      }

      // Start the requested tour (if we're already in the app) and prevent the 'navigation'
      if (from.name) {
        window.Intercom("startTour", tourId);
        return false;
      }

      // Otherwise, wait for Intercom to load (after the app has loaded) and then start the tour
      onIntercomQueueReady(() => window.Intercom("startTour", tourId));

      return defaultAppRoute();
    },
  },
  { name: "logout", path: "/logout", meta: { needsAuth: false }, component: LogoutPage },
  {
    name: "all",
    path: "/:pathMatch(.*)*",
    redirect: defaultAppRoute(),
  },
  { name: "wheelTest", path: "/wheeltest", meta: { needsAuth: false }, component: WheelTestPage },
];

function defaultAppRoute() {
  return {
    name: "app",
    params: { session: PARAM.INITIAL, team: PARAM.INITIAL, board: PARAM.INITIAL },
  };
}
